import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Paper,
  TextField,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import api from "../../api";
import { toast } from "react-toastify";

const PURPLE = "#7c3aed";

function StudentTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAnswerUnit, setShowAnswerUnit] = useState(true);
  const [mcqNoticeMessage, setMcqNoticeMessage] = useState("");
  const [mcqNoticeDefault, setMcqNoticeDefault] = useState("");

  useEffect(() => {
    api.get("admin/analytics/student-settings")
      .then((res) => {
        setShowAnswerUnit(!!res.data?.data?.show_answer_unit);
        setMcqNoticeMessage(res.data?.data?.mcq_notice_message || "");
        setMcqNoticeDefault(res.data?.data?.mcq_notice_message_default || "");
      })
      .catch(() => toast.error("Failed to load student settings."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("admin/analytics/student-settings", {
        show_answer_unit: showAnswerUnit,
        mcq_notice_message: mcqNoticeMessage.trim() || null,
      });
      if (res.data?.data?.mcq_notice_message) setMcqNoticeMessage(res.data.data.mcq_notice_message);
      toast.success("Student settings saved.");
    } catch {
      toast.error("Failed to save student settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Answer Units
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, the expected unit (e.g. cm, kg, £) is shown next to the student&apos;s
            answer input for short-answer questions. The student types only the numeric value;
            the unit is appended automatically on submission. Units apply to Maths questions only
            — English, Verbal and Non-Verbal Reasoning answers never show a unit.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={showAnswerUnit}
                  onChange={(e) => setShowAnswerUnit(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Show answer units to students
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showAnswerUnit ? "Units are currently visible." : "Units are currently hidden."}
                  </Typography>
                </Box>
              }
            />
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Convert-to-MCQ Notice
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Message shown in the popup when a student first opens a paper or mock exam containing
            questions flagged as &quot;Recommend convert to MCQ&quot; (hard to answer online). The
            list of flagged question numbers is added below this message automatically. Leave empty
            to use the default message.
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Popup message"
                value={mcqNoticeMessage}
                onChange={(e) => setMcqNoticeMessage(e.target.value)}
                inputProps={{ maxLength: 2000 }}
                disabled={saving}
                placeholder={mcqNoticeDefault}
              />
              {mcqNoticeDefault && mcqNoticeMessage.trim() !== mcqNoticeDefault && (
                <Button
                  size="small"
                  sx={{ mt: 1, textTransform: "none" }}
                  onClick={() => setMcqNoticeMessage(mcqNoticeDefault)}
                  disabled={saving}
                >
                  Reset to default message
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        Changes take effect immediately for new page loads — no server restart needed.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}

function ParentTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowAnswerKeyDownload, setAllowAnswerKeyDownload] = useState(true);

  useEffect(() => {
    api.get("admin/analytics/parent-settings")
      .then((res) => setAllowAnswerKeyDownload(!!res.data?.data?.allow_answer_key_download))
      .catch(() => toast.error("Failed to load parent settings."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("admin/analytics/parent-settings", {
        allow_answer_key_download: allowAnswerKeyDownload,
      });
      toast.success("Parent settings saved.");
    } catch {
      toast.error("Failed to save parent settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Answer Key Downloads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When enabled, parents can download answer keys for past papers, mock exams, and worksheets
            (the &quot;With Answers&quot; worksheet download).
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={allowAnswerKeyDownload}
                  onChange={(e) => setAllowAnswerKeyDownload(e.target.checked)}
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Allow parents to download answer keys
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {allowAnswerKeyDownload ? "Parents can download answer keys." : "Answer key downloads are hidden and blocked."}
                  </Typography>
                </Box>
              }
            />
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
        Changes take effect immediately for new page loads — no server restart needed.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}

function PlaceholderTab({ label }) {
  return (
    <Box sx={{ maxWidth: 640 }}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {label} settings are not yet configured.
      </Alert>
    </Box>
  );
}

const TABS = ["Student", "Parent", "Tutor", "Tester", "System"];

const AdminSettings = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Platform-wide configuration settings.
        </Typography>
      </Box>

      <Paper
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid #eef0f4",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            px: { xs: 0.5, sm: 1 },
            bgcolor: "#fafafa",
            borderBottom: "1px solid #eef0f4",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              minHeight: 48,
              color: "#64748b",
            },
            "& .Mui-selected": { color: PURPLE },
            "& .MuiTabs-indicator": { backgroundColor: PURPLE, height: 3 },
          }}
        >
          {TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tab === 0 && <StudentTab />}
          {tab === 1 && <ParentTab />}
          {tab === 2 && <PlaceholderTab label="Tutor" />}
          {tab === 3 && <PlaceholderTab label="Tester" />}
          {tab === 4 && <PlaceholderTab label="System" />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminSettings;
